import React, { useState } from 'react'
import { Tag, Pagination, Popconfirm, message, Tooltip } from 'antd'
import { EditOutlined, DeleteOutlined, BankOutlined } from '@ant-design/icons'
import Table from '../../../components/common/table/table'
import Button from '../../../components/common/button/button'
import Spinner from '../../../components/common/spinner/spinner'

const DraftsComponent = ({
  drafts,
  onEditDraft,
  onDeleteDraft,
  loading = false,
  totalRecords = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onRefreshDrafts,
  currentUserEmail,
  onCostCenterClick
}) => {
  const [deletingIds, setDeletingIds] = useState(new Set())

  // Helper function to check if current user can edit/delete
  const canModifyDraft = (draft) => {
    if (!currentUserEmail || !draft.createdByEmail) return false
    return draft.createdByEmail.toLowerCase() === currentUserEmail.toLowerCase()
  }

  // Helper function to render header with tooltip
  const renderHeaderWithTooltip = (title, maxLength = 15) => {
    if (title.length <= maxLength) {
      return title
    }

    return (
      <Tooltip title={title} placement="top">
        <span>{title.substring(0, maxLength)}...</span>
      </Tooltip>
    )
  }

  // Handle delete draft with loading state
  const handleDeleteDraft = async (record) => {
    const transactionId = record.rfpId || record.transactionId || record.id

    if (!transactionId) {
      message.error('Unable to delete: Transaction ID not found')
      return
    }

    setDeletingIds(prev => new Set(prev).add(transactionId))

    try {
      await onDeleteDraft(transactionId)
      if (onRefreshDrafts) {
        onRefreshDrafts()
      }
    } catch (error) {
      console.error('Error deleting draft:', error)
      message.error(error.message || 'Failed to delete draft')
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(transactionId)
        return newSet
      })
    }
  }

  // Drafts table columns - matching RFP table structure
  const draftColumns = [
    {
      title: 'Sr. No',
      key: 'srNo',
      width: 80,
      fixed: 'left',
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: 'RFP Id',
      dataIndex: 'rfpId',
      key: 'rfpId',
      width: 150,
      fixed: 'left'
    },
    {
      title: renderHeaderWithTooltip('Vendor Name', 12),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 150
    },
    {
      title: renderHeaderWithTooltip('Requested Amount', 12),
      dataIndex: 'requestedAmount',
      key: 'requestedAmount',
      width: 150,
      render: (amount, record) => {
        // Use currencySymbol from transformDraftData
        const symbol = record.currencySymbol || '₱'
        return `${symbol}${amount.toLocaleString()}`
      }
    },
    {
      title: renderHeaderWithTooltip('Date of Creation', 12),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      width: 130
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150
    },
    {
      title: 'Categories',
      dataIndex: 'categories',
      key: 'categories',
      width: 200,
      ellipsis: true
    },
    {
      title: renderHeaderWithTooltip('Invoice Ref No', 12),
      dataIndex: 'invoiceReferenceNumber',
      key: 'invoiceReferenceNumber',
      width: 150,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: renderHeaderWithTooltip('File Prepared For', 12),
      dataIndex: 'filePreparedFor',
      key: 'filePreparedFor',
      width: 150,
      ellipsis: true
    },
    {
      title: renderHeaderWithTooltip('Preferred Payment Method', 15),
      dataIndex: 'preferredPaymentMethod',
      key: 'preferredPaymentMethod',
      width: 180,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color="orange" className="status-tag-with-border">
          {status}
        </Tag>
      )
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      width: 150,
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_, record) => {
        const transactionId = record.rfpId || record.transactionId || record.id
        const isDeleting = deletingIds.has(transactionId)
        const canModify = canModifyDraft(record)

        return (
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
            {/* Cost Center Icon */}
            {onCostCenterClick && record.costCenterData && (
              <Tooltip title="View Cost Center & Purpose">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.3s'
                  }}
                  onClick={() => onCostCenterClick(record)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(173, 2, 112, 0.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                >
                  <BankOutlined style={{ color: '#ad0270', fontSize: '18px' }} />
                </div>
              </Tooltip>
            )}

            {/* Edit Button */}
            <Tooltip title={canModify ? 'Edit Draft' : 'You can only edit your own drafts'}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => canModify && onEditDraft(record)}
                disabled={isDeleting || !canModify}
                style={{
                  color: (!canModify || isDeleting) ? '#d9d9d9' : '#1890ff',
                  padding: '4px 8px',
                  height: 'auto',
                  cursor: canModify ? 'pointer' : 'not-allowed'
                }}
              />
            </Tooltip>

            {/* Delete Button */}
            <Popconfirm
              title="Delete Draft"
              description={
                <div>
                  <div>Are you sure you want to delete this draft?</div>
                </div>
              }
              onConfirm={() => handleDeleteDraft(record)}
              okText="Yes"
              cancelText="Cancel"
              okButtonProps={{
                danger: true,
                loading: isDeleting
              }}
              disabled={isDeleting || !canModify}
            >
              <Tooltip title={canModify ? 'Delete Draft' : 'You can only delete your own drafts'}>
                <Button
                  type="text"
                  icon={isDeleting ? <Spinner size="small" /> : <DeleteOutlined />}
                  disabled={isDeleting || !canModify}
                  style={{
                    color: (!canModify || isDeleting) ? '#d9d9d9' : '#ff4d4f',
                    padding: '4px 8px',
                    height: 'auto',
                    cursor: canModify ? 'pointer' : 'not-allowed'
                  }}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        )
      }
    }
  ]

  // Loading state component
  const loadingState = (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px'
    }}>
      <Spinner size="large" />
    </div>
  )

  // Empty state component
  const emptyState = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      padding: '40px 20px',
      color: '#8c8c8c'
    }}>
      <svg
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginBottom: '16px', opacity: 0.6 }}
      >
        <rect x="15" y="25" width="50" height="35" rx="3" stroke="#d9d9d9" strokeWidth="2" fill="none" />
        <rect x="20" y="30" width="40" height="25" rx="1" stroke="#d9d9d9" strokeWidth="1.5" fill="none" />
        <path d="M25 20 L35 15 L45 15 L55 20" stroke="#d9d9d9" strokeWidth="2" fill="none" />
        <circle cx="30" cy="40" r="2" fill="#d9d9d9" />
        <circle cx="40" cy="40" r="2" fill="#d9d9d9" />
        <circle cx="50" cy="40" r="2" fill="#d9d9d9" />
      </svg>
      <span style={{ fontSize: '16px', fontWeight: 500 }}>No drafts available</span>
      <span style={{ fontSize: '14px', marginTop: '4px', opacity: 0.8 }}>
        Create your first draft to get started
      </span>
    </div>
  )

  const handlePageChange = (page, size) => {
    if (onPageChange) {
      onPageChange(page, size)
    }
  }

  const handlePageSizeChange = (current, size) => {
    if (onPageChange) {
      onPageChange(1, size)
    }
  }

  return (
    <div className="drafts-component">
      <div style={{ marginTop: '24px' }}>
        <Table
          columns={draftColumns}
          dataSource={loading ? [] : drafts}
          rowKey="id"
          scroll={{ x: 1800, y: 'calc(100vh - 500px)' }}
          pagination={false}
          className="drafts-component__table"
          sticky={{ offsetHeader: 0 }}
          bordered={false}
          size="middle"
          locale={{
            emptyText: loading ? loadingState : emptyState
          }}
        />

        {!loading && totalRecords > 0 && (
          <div className="drafts-component__pagination" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', paddingRight: '24px' }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              showSizeChanger={true}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} Drafts`
              }
              pageSizeOptions={['5', '10', '20', '50']}
              onChange={handlePageChange}
              onShowSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftsComponent
